import React, { useState } from 'react';
import { X } from 'lucide-react';
import { apiCall } from './invoiceApi';

/**
 * Payment Modal
 * Modal for recording payments on invoices
 */
const PaymentModal = ({ invoice, language, onClose, onSave }) => {
  const [amount, setAmount] = useState(invoice.amount_due || invoice.total_incl || 0);
  const [paymentDate, setPaymentDate] = useState(new Date().toLocaleDateString('fr-BE'));
  const [paymentMethod, setPaymentMethod] = useState('virement');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall(`/api/invoicing/invoices/${invoice.id}/payments`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(amount),
          payment_date: paymentDate,
          payment_method: paymentMethod,
          reference,
          notes,
        }),
      });
      onSave();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const t = language === 'fr' ? {
    title: 'Enregistrer un paiement',
    invoice: 'Facture',
    totalDue: 'Montant dû',
    amount: 'Montant reçu',
    date: 'Date de paiement',
    method: 'Mode de paiement',
    methods: { virement: 'Virement', cash: 'Espèces', carte: 'Carte', autre: 'Autre' },
    reference: 'Référence',
    notes: 'Notes',
    save: 'Enregistrer',
    cancel: 'Annuler',
  } : {
    title: 'Betaling registreren',
    invoice: 'Factuur',
    totalDue: 'Te betalen',
    amount: 'Ontvangen bedrag',
    date: 'Betaaldatum',
    method: 'Betaalmethode',
    methods: { virement: 'Overschrijving', cash: 'Contant', carte: 'Kaart', autre: 'Andere' },
    reference: 'Referentie',
    notes: 'Opmerkingen',
    save: 'Opslaan',
    cancel: 'Annuleren',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">{t.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">{t.invoice}: <span className="font-medium text-gray-900">{invoice.invoice_number}</span></p>
            <p className="text-sm text-gray-500">{t.totalDue}: <span className="font-medium text-amber-700">€{(invoice.amount_due || invoice.total_incl || 0).toFixed(2)}</span></p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.amount} (€)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.date}</label>
            <input
              type="text"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              placeholder="DD/MM/YYYY"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.method}</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {Object.entries(t.methods).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.reference}</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="VIR-123456"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.notes}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
            >
              {saving ? '...' : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
